import { ServiceKind } from '@ail/contracts-api'
import { Column } from 'primereact/column'
import { ColumnGroup } from 'primereact/columngroup'
import { DataTable } from 'primereact/datatable'
import { Row } from 'primereact/row'
import { useCallback, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { NavLink, useLocation, useParams } from 'react-router-dom'
import DateSlider from '../../common/DateSlider/DateSlider'
import './CostDetails.scss'
import { Arr } from '../../../helpers/Arr'
import { useAuth } from '../../../hooks/useAuth'
import { ContractProduct, Contracts } from '../../../utils/contracts'
import { useGetContractQuery } from '../../../redux/api/contractsApi'

export function CostDetails(props: {
  costs: {
    totalExpected: number
    totalActual: number
    totalDifference: number
    byKind: [ServiceKind, number, number, number][]
  }
  interval: { from: Date; to: Date }
  range?: { from: Date; to: Date }
  onIntervalChange: (from: Date, to: Date) => void
  excludeRestricted: boolean
}) {
  const { t } = useTranslation('common')
  const location = useLocation()
  const { user } = useAuth()
  const { id: contractId } = useParams() as { id: string }
  const contract = useGetContractQuery(contractId)

  const isFullClosed = useMemo(
    () => (contract.data ? Contracts.getContractProduct(contract.data) === ContractProduct.FullClosed : false),
    [contract]
  )
  const formatNumber = (value: number) => {
    return new Intl.NumberFormat('de-CH', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(value)
  }

  const getCostKindIcon = (kind: ServiceKind) => {
    switch (kind) {
      case ServiceKind.Rmt:
        return kind?.toLowerCase() // TODO will be replaced with icon class name when icon added
      case ServiceKind.CarWash:
        return 'car-wash'
      case ServiceKind.RoadTax:
        return 'road-tax'
      case ServiceKind.ReliefVehicle:
        return 'reliefVehicle'
      case ServiceKind.Reestablishment:
        return 'car-outfit'
      case ServiceKind.Refurbishment:
        return 'car-sponge'
      default:
        return kind.toLowerCase()
    }
  }

  const getCostKindLabel = useCallback(
    (kind: ServiceKind) => {
      switch (kind) {
        case ServiceKind.Rmt:
          return t('MAINTENANCE')
        default:
          return t(kind.toUpperCase())
      }
    },
    [t]
  )

  const costsByKind = useMemo(() => {
    return Arr.sort(Object.values(props.costs.byKind), (c) => getCostKindLabel(c[0]))
  }, [getCostKindLabel, props.costs.byKind])
  const costKindTemplate = (serviceKind: ServiceKind) => {
    const icon = getCostKindIcon(serviceKind)
    const label = getCostKindLabel(serviceKind)
    // if rmt or tires, return plain span UI
    // internal still clickable
    const restrictedKind =
      isFullClosed && (serviceKind === ServiceKind.Rmt || serviceKind === ServiceKind.Tires) && !user?.isInternalUser
    const redirectable = (
      <NavLink
        style={{ display: 'flex', alignItems: 'center' }}
        to={{
          pathname: `${location.pathname}/${serviceKind}`,
          state: { from: props.interval.from, to: props.interval.to },
        }}
      >
        <i className={`icon-${icon}`} /> {label}
      </NavLink>
    )
    const basic = (
      <span style={{ display: 'flex', alignItems: 'center' }}>
        <i className={`icon-${icon}`} /> {label}
      </span>
    )
    return restrictedKind ? basic : redirectable
  }

  const totalFooterTemplate = () => {
    return (
      <ColumnGroup>
        <Row>
          <Column footer={t('TOTAL')} />
          <Column footer={formatNumber(props.costs.totalActual)} />
          <Column footer={formatNumber(props.costs.totalExpected)} />
          <Column footer={formatNumber(props.costs.totalDifference)} />
        </Row>
      </ColumnGroup>
    )
  }

  return (
    <div className="detailContentArea">
      <div className={`box-title`}>
        <span>
          <i className="icon-cost" /> <span>{t('COSTS')}</span>
        </span>
      </div>
      <div className="docDetails contrDetails">
        <div className="detail-container">
          <div className="detail-container__slider">
            <DateSlider
              onUpdate={(i) => props.onIntervalChange(i.from, i.to)}
              includeLastYear={true}
              startDate={props.interval.from}
              endDate={props.interval.to}
              minDateInSlider={props.range?.from}
              maxDateInSLider={props.range?.to}
              wholeRangeDrag={true}
            />
          </div>
          <div className={`detail-container__grid costs ${costsByKind.length ? 'print' : 'no-print'}`}>
            <DataTable
              footerColumnGroup={totalFooterTemplate()}
              value={costsByKind}
              emptyMessage={t('NO_RECORDS_FOUND')}
              loading={false}
            >
              <Column field="kind" header={t('KIND')} body={(row) => costKindTemplate(row[0])} />
              <Column field="actual" header={t('ACTUAL')} body={(row) => formatNumber(row[1])} />
              <Column field="expected" header={t('EXPECTED')} body={(row) => formatNumber(row[2])} />
              <Column field="difference" header={t('DIFFERENCE')} body={(row) => formatNumber(row[3])} />
            </DataTable>
          </div>
        </div>
      </div>
    </div>
  )
}
